// Pure maths for the WSI <-> SectionStar synced view (phase1-prototype-plan.md).
//
// Deliberately free of OpenSeadragon, scss and svg imports so it runs under Jest's
// default node environment. Everything here works in IMAGE PIXELS of each image:
// y points down, and angles are degrees, positive = clockwise on screen, which is
// OpenSeadragon's rotation convention. R(θ) = [[cos, -sin], [sin, cos]].
//
// The transform T always maps WSI image pixels -> SectionStar image pixels:
//   p_ss = k · R(θ) · p_wsi + t,   k = mpp_wsi / mpp_ss

// Nominal SectionStar resolutions, fixed by the machine's mechanical design
// (architecture-syncview.md §6.3.0). Frontend-only for phase 1; should move to the
// backend adapter later.
export const SECTIONSTAR_STATE_MPP = { sectioning: 9, tape: 14, slide: 14 };

// Used when a slide carries no resolution metadata at all. BIG ASSUMPTION: treats
// the slide as an Aperio 40x scan. See phase1-prototype-plan.md step 3.
export const ASSUMED_WSI_MPP = 0.25;

const positive = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * µm per pixel of the whole slide image, from the vips-properties.xml metadata.
 * @returns {{ mpp: number, assumed: boolean, source: string }}
 */
export const getWsiMpp = (meta = {}) => {
  const openslide = positive(meta["openslide.mpp-x"]);
  if (openslide) return { mpp: openslide, assumed: false, source: "openslide.mpp-x" };

  const aperio = positive(meta["aperio.MPP"]);
  if (aperio) return { mpp: aperio, assumed: false, source: "aperio.MPP" };

  const objective = positive(meta["openslide.objective-power"]);
  if (objective) {
    return { mpp: 10 / objective, assumed: true, source: "objective-power" };
  }
  return { mpp: ASSUMED_WSI_MPP, assumed: true, source: "default" };
};

/** µm per pixel of a SectionStar image, by its state; null when unknown. */
export const getSectionStarMpp = (state) => SECTIONSTAR_STATE_MPP[state] || null;

/**
 * OSD image zoom (screen px per image px) that shows the same µm per screen pixel.
 * µm per screen px = mpp / imageZoom, so imageZoom_to = imageZoom_from × mpp_to / mpp_from.
 */
export const matchImageZoom = (imageZoom, mppFrom, mppTo) => (imageZoom * mppTo) / mppFrom;

const DEG = Math.PI / 180;

export const makeTransform = (k, thetaDeg, tx, ty) => ({ k, theta: thetaDeg, tx, ty });

/** WSI image px -> SectionStar image px. */
export const applyTransform = (T, p) => {
  const c = Math.cos(T.theta * DEG);
  const s = Math.sin(T.theta * DEG);
  return {
    x: T.k * (c * p.x - s * p.y) + T.tx,
    y: T.k * (s * p.x + c * p.y) + T.ty,
  };
};

/** SectionStar image px -> WSI image px. */
export const applyInverse = (T, p) => {
  const c = Math.cos(T.theta * DEG);
  const s = Math.sin(T.theta * DEG);
  const dx = (p.x - T.tx) / T.k;
  const dy = (p.y - T.ty) / T.k;
  // R(θ)^-1 = R(-θ)
  return { x: c * dx + s * dy, y: -s * dx + c * dy };
};

// t = c_ss - k·R(θ)·c_wsi
const translationFor = (k, thetaDeg, from, to) => {
  const rotated = applyTransform({ k, theta: thetaDeg, tx: 0, ty: 0 }, from);
  return { tx: to.x - rotated.x, ty: to.y - rotated.y };
};

/**
 * Transform from the current views: the two pane centres are taken to show the same
 * tissue, and the on-screen orientation is taken to match.
 */
export const buildAnchorTransform = ({ cWsi, cSs, rhoWsi, rhoSs, k }) => {
  const theta = rhoWsi - rhoSs;
  const { tx, ty } = translationFor(k, theta, cWsi, cSs);
  return makeTransform(k, theta, tx, ty);
};

const mean = (points) => {
  const sum = points.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
  return { x: sum.x / points.length, y: sum.y / points.length };
};

/**
 * Rotation + translation from corresponding points, with the scale k fixed.
 * 1 pair: translation only, keeping fallbackThetaDeg. 2+ pairs: least-squares θ.
 * @returns {{ T, residualPx: number|null, pairs: number }|null}
 */
export const fitLandmarks = (wsiPoints, ssPoints, k, fallbackThetaDeg = 0) => {
  const pairs = Math.min(wsiPoints.length, ssPoints.length);
  if (pairs < 1) return null;
  const a = wsiPoints.slice(0, pairs);
  const b = ssPoints.slice(0, pairs);
  const meanA = mean(a);
  const meanB = mean(b);

  let theta = fallbackThetaDeg;
  if (pairs >= 2) {
    let cross = 0;
    let dot = 0;
    for (let i = 0; i < pairs; i += 1) {
      const ax = a[i].x - meanA.x;
      const ay = a[i].y - meanA.y;
      const bx = b[i].x - meanB.x;
      const by = b[i].y - meanB.y;
      cross += ax * by - ay * bx;
      dot += ax * bx + ay * by;
    }
    theta = Math.atan2(cross, dot) / DEG;
  }

  const { tx, ty } = translationFor(k, theta, meanA, meanB);
  const T = makeTransform(k, theta, tx, ty);

  let residualPx = null;
  if (pairs >= 3) {
    const squares = a.map((p, i) => {
      const q = applyTransform(T, p);
      return (q.x - b[i].x) ** 2 + (q.y - b[i].y) ** 2;
    });
    residualPx = Math.sqrt(squares.reduce((s, v) => s + v, 0) / pairs);
  }
  return { T, residualPx, pairs };
};

// Landmark alignment needs this many pairs: 2 fix rotation + translation, the third
// gives an error estimate and lets the scale be checked.
export const MIN_LANDMARK_PAIRS = 3;

// How far the scale measured from the points may differ from the nominal µm/px
// before it is treated as "completely different" and used instead. Ordinary
// section shrinkage/stretch and click error stay inside this.
export const SCALE_TOLERANCE = 1.25;

// Below this spread (RMS distance of the SectionStar points from their centroid, in
// SectionStar pixels) the points are too close together to measure scale.
export const MIN_SCALE_SPREAD_PX = 20;

/**
 * Scale (k) measured from corresponding points with rotation + translation + free
 * scale (least-squares similarity). null when it cannot be measured.
 */
export const measureLandmarkScale = (wsiPoints, ssPoints) => {
  const pairs = Math.min(wsiPoints.length, ssPoints.length);
  if (pairs < 2) return null;
  const a = wsiPoints.slice(0, pairs);
  const b = ssPoints.slice(0, pairs);
  const meanA = mean(a);
  const meanB = mean(b);
  let cross = 0;
  let dot = 0;
  let normA = 0;
  let normB = 0;
  for (let i = 0; i < pairs; i += 1) {
    const ax = a[i].x - meanA.x;
    const ay = a[i].y - meanA.y;
    const bx = b[i].x - meanB.x;
    const by = b[i].y - meanB.y;
    cross += ax * by - ay * bx;
    dot += ax * bx + ay * by;
    normA += ax * ax + ay * ay;
    normB += bx * bx + by * by;
  }
  const spreadPx = Math.sqrt(normB / pairs);
  if (!normA || spreadPx < MIN_SCALE_SPREAD_PX) return null;
  return { k: Math.sqrt(cross * cross + dot * dot) / normA, spreadPx };
};

/**
 * Landmark fit for the "Align with points" flow.
 *
 * Positions: rotation, translation AND scale come from the points whenever the scale
 * can be measured. Even a few percent of scale error shifts the panes apart in
 * proportion to the distance from the points' centre, so positions always use the
 * measured scale (T.k).
 *
 * Displayed zoom: stays at the nominal scale unless the points say it is completely
 * different (outside SCALE_TOLERANCE). Carried as T.kZoom.
 *
 * Falls back to the nominal scale for both when the points are too close together
 * to measure it.
 * @returns {{ T, residualPx, pairs, scaleCheck }|null}
 */
export const fitLandmarksWithScaleCheck = (wsiPoints, ssPoints, kNominal) => {
  const pairs = Math.min(wsiPoints.length, ssPoints.length);
  if (pairs < MIN_LANDMARK_PAIRS) return null;

  const measured = measureLandmarkScale(wsiPoints, ssPoints);
  const ratio = measured ? measured.k / kNominal : null;
  const zoomUsesMeasured =
    ratio !== null && (ratio > SCALE_TOLERANCE || ratio < 1 / SCALE_TOLERANCE);

  const fit = fitLandmarks(wsiPoints, ssPoints, measured ? measured.k : kNominal);
  return {
    ...fit,
    T: { ...fit.T, kZoom: zoomUsesMeasured ? measured.k : kNominal },
    scaleCheck: {
      // measured k / nominal k; < 1 means each SectionStar pixel covers more tissue
      // than the nominal µm/px says. null = could not measure.
      ratio,
      // which scale the displayed zoom uses; positions use the measured one if any
      used: zoomUsesMeasured ? "measured" : "nominal",
    },
  };
};

// Alignment quality thresholds. Fit error is the RMS distance in µm between where
// the fit puts each slide point and where the user clicked it on the SectionStar
// image; the scale ratio is the points' measured scale over the nominal µm/px.
export const QUALITY_GOOD_FIT_UM = 50; // a few SectionStar pixels (9-14 µm/px)
export const QUALITY_FAIR_FIT_UM = 150;
export const QUALITY_GOOD_SCALE = 0.1; // ±10%: typical calibration/section shrinkage
// Fair scale limit is SCALE_TOLERANCE (±25%), past which the nominal is "completely
// different" from what the points say.

/**
 * One simple status from the landmark fit: "good" (green), "fair" (yellow) or
 * "poor" (red). The worse of the two measures wins. A scale that could not be
 * measured (points too close together) is at best "fair", since the edges may drift.
 */
export const alignmentQuality = ({ residualUm, scaleRatio }) => {
  const rank = { good: 0, fair: 1, poor: 2 };
  const worst = (a, b) => (rank[a] >= rank[b] ? a : b);

  let fit = "good";
  if (residualUm !== null && residualUm !== undefined) {
    if (residualUm > QUALITY_FAIR_FIT_UM) fit = "poor";
    else if (residualUm > QUALITY_GOOD_FIT_UM) fit = "fair";
  }

  let scale = "fair";
  if (scaleRatio !== null && scaleRatio !== undefined) {
    const off = Math.abs(scaleRatio - 1);
    if (scaleRatio > SCALE_TOLERANCE || scaleRatio < 1 / SCALE_TOLERANCE) scale = "poor";
    else if (off > QUALITY_GOOD_SCALE) scale = "fair";
    else scale = "good";
  }

  return worst(fit, scale);
};

/** SectionStar pane rotation that shows the tissue in the same orientation as the WSI pane. */
export const followerRotation = (rhoWsi, thetaDeg) => rhoWsi - thetaDeg;

/**
 * On-screen size of the WSI's field of view drawn on the SectionStar pane, once the
 * SectionStar pane is held at its zoom limit (izMax) but would need izWanted.
 */
export const fovBoxSize = ({ paneW, paneH, izMax, izWanted, minPx = 8 }) => {
  const ratio = izMax / izWanted;
  return {
    width: Math.max(minPx, paneW * ratio),
    height: Math.max(minPx, paneH * ratio),
  };
};

/** Physical size in µm of what the WSI pane currently shows. */
export const fovMicrons = (paneW, paneH, mppWsi, imageZoomWsi) => ({
  width: (paneW * mppWsi) / imageZoomWsi,
  height: (paneH * mppWsi) / imageZoomWsi,
});

/** "120 × 80 µm" / "1.2 × 0.8 mm" */
export const formatFov = ({ width, height }) => {
  if (Math.max(width, height) >= 1000) {
    return `${(width / 1000).toFixed(2)} × ${(height / 1000).toFixed(2)} mm`;
  }
  return `${Math.round(width)} × ${Math.round(height)} µm`;
};
